import { Button, Popover, Tag, Tooltip, Input } from 'antd';
import cx from 'classnames';
import { observer } from 'mobx-react';
// 使用HTML参考中的SVG图标
import {
    LineChartIcon,
    SurfaceChartIcon,
    PieChartIcon,
    BarChartIcon,
    ScatterChartIcon,
    BoxChartIcon,
    FlowchartIcon,
    MindMapIcon,
    OrgChartIcon,
    UseCaseIcon,
    ClassDiagramIcon,
    StateDiagramIcon,
    SequenceDiagramIcon,
    NetworkDiagramIcon,
    CustomChartIcon,
} from './chartIcons';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import styles from './Operator.module.scss';
import { DataIcon, PicIcon, GongshiIcon, CodeIcon, TableIcon } from './icons';
import { DOCTYPEENUM, DocType } from '../const';

const AddIcon = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="img"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        className={styles.optIcon}
        style={{ color: 'green' }}
    >
        <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 16v3m0 0v3m0-3h3m-3 0h-3m4-10v-.172a2 2 0 0 0-.586-1.414l-3.828-3.828A2 2 0 0 0 14.172 3H14m6 6h-4a2 2 0 0 1-2-2V3m6 6v3m-6-9H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"
        ></path>
    </svg>
);

const EditIcon = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="img"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        className={styles.optIcon}
        style={{ color: 'rgb(59, 130, 246)' }}
    >
        <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1l1-4Z"
        ></path>
    </svg>
);
const DeleteIcon = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="img"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        className={styles.optIcon}
        style={{ color: 'rgb(212, 2, 2)' }}
    >
        <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 7h16m-10 4v6m4-6v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
        ></path>
    </svg>
);

// 图表类型枚举
export const ChartType = {
    // 数据分析图表
    LINE: 'line',
    SURFACE: 'surface',
    PIE: 'pie',
    BAR: 'bar',
    SCATTER: 'scatter',
    BOX: 'box',
    // 结构与流程图
    FLOWCHART: 'flowchart',
    MIND_MAP: 'mindmap',
    ORG_CHART: 'org_chart',
    USE_CASE: 'usecase',
    // 技术与专业图
    CLASS_DIAGRAM: 'class_diagram',
    STATE_DIAGRAM: 'state_diagram',
    SEQUENCE_DIAGRAM: 'sequence_diagram',
    NETWORK_DIAGRAM: 'network_diagram',
    // 自定义类型
    OTHER: 'other',
};

// 图表类型分组
const CHART_GROUPS = [
    {
        title: '数据分析图表',
        charts: [
            { type: ChartType.LINE, label: '折线图', icon: LineChartIcon },
            { type: ChartType.SURFACE, label: '曲面图', icon: SurfaceChartIcon },
            { type: ChartType.PIE, label: '饼图', icon: PieChartIcon },
            { type: ChartType.BAR, label: '柱状图', icon: BarChartIcon },
            { type: ChartType.SCATTER, label: '散点图', icon: ScatterChartIcon },
            { type: ChartType.BOX, label: '箱型图', icon: BoxChartIcon },
        ],
    },
    {
        title: '结构与流程图',
        charts: [
            { type: ChartType.FLOWCHART, label: '流程图', icon: FlowchartIcon },
            { type: ChartType.MIND_MAP, label: '思维导图', icon: MindMapIcon },
            { type: ChartType.ORG_CHART, label: '组织结构图', icon: OrgChartIcon },
            { type: ChartType.USE_CASE, label: '用例图', icon: UseCaseIcon },
        ],
    },
    {
        title: '技术与专业图',
        charts: [
            { type: ChartType.CLASS_DIAGRAM, label: '类图', icon: ClassDiagramIcon },
            { type: ChartType.STATE_DIAGRAM, label: '状态图', icon: StateDiagramIcon },
            { type: ChartType.SEQUENCE_DIAGRAM, label: '时序图', icon: SequenceDiagramIcon },
            { type: ChartType.NETWORK_DIAGRAM, label: '网络图', icon: NetworkDiagramIcon },
        ],
    },
];

// 图表类型到图标的映射
const CHART_ICON_MAP = {};
CHART_GROUPS.forEach((group) => {
    group.charts.forEach((chart) => {
        CHART_ICON_MAP[chart.type] = chart.icon;
    });
});

/** 判断额外按钮的状态 */
function judgeIcon(data, type, options = {}) {
    const { isLevel1 = false, isKaitiOrZongshu = false, isLastLevel = false } = options;
    const TYPES_DESC = {
        CODE: '代码',
        TABLE: '数据(表)',
        DATA: '数据',
        DIAGRAM: '图',
        FORMULA: '公式',
    };
    const { extraType = '', selected_extra = '', extraTypeCn } = data || {};

    // 对于一级大纲，在开题报告和文献综述时，且为最后一级时，表和图按钮即使没有 extraType 也允许显示
    const isTableOrDiagram = type === 'TABLE' || type === 'DIAGRAM';
    const canShowWithoutExtraType = isLevel1 && isKaitiOrZongshu && isLastLevel && isTableOrDiagram;

    if (!extraType && !canShowWithoutExtraType) {
        return {
            canShow: false,
        };
    }
    // const canShow = extraType?.split(',').includes(type) && +data.docType === DOCTYPEENUM.PAPER;
    const canShow = true;
    const have = selected_extra?.split(',').includes(type);
    const tip = !have
        ? '点亮图标，本节插入' + TYPES_DESC[type]
        : extraTypeCn && extraTypeCn[type]
          ? extraTypeCn[type]
          : '本节插入' + TYPES_DESC[type];
    return {
        canShow,
        have,
        tip,
    };
}

export const Operator = observer((props) => {
    const {
        onEdit,
        onAdd,
        onDelete,
        isLastLevel,
        data,
        onChangeExtra,
        docType,
        hideEdit,
        hideDelete,
        allowedExtraTypes, // 允许显示的付费项类型数组，如 ['TABLE', 'DIAGRAM']
        className,
        modelVersion = 'NORMAL', // 模型版本: NORMAL, premium
    } = props;
    const { extraType, selected_extra, extraTypeCn, selectedChartType, chartTypeCn } = data || {};
    const [chartPopoverVisible, setChartPopoverVisible] = useState(false);
    const [otherChartInputValue, setOtherChartInputValue] = useState(chartTypeCn?.other || '');
    // 展开的图表类型（用于编辑自定义图表）
    const [expandedChartType, setExpandedChartType] = useState(null);
    // 搜索关键词
    const [searchKeyword, setSearchKeyword] = useState('');

    // 使用 useState 保存后端是否返回过 other 的初始状态（一旦检测到就永远保存）
    // 使用 useRef 作为辅助，确保每个章节只设置一次
    const hasOtherFromBackendRef = useRef({});
    const [hasOtherFromBackend, setHasOtherFromBackend] = useState(false);
    // textarea 的 ref，用于控制光标位置
    const customChartTextareaRef = useRef(null);
    // 记录是否已经初始化过展开状态（避免用户手动收起后再次自动展开）
    const hasInitializedExpandRef = useRef(false);

    // 获取当前章节的唯一标识（用于区分不同章节）
    const chapterKey = useMemo(() => {
        // 使用 chapter 作为唯一标识，如果没有则使用其他唯一字段
        return data?.chapter || data?.id || JSON.stringify(data);
    }, [data]);

    // 解析 selectedChartType（可能是逗号分隔的字符串）
    const selectedChartTypes = useMemo(() => {
        if (!selectedChartType) return [];
        return typeof selectedChartType === 'string'
            ? selectedChartType.split(',').filter(Boolean)
            : Array.isArray(selectedChartType)
              ? selectedChartType
              : [];
    }, [selectedChartType]);

    // 判断是否显示自定义图分类
    // Premium 模式：始终显示
    // Normal/Ultra 模式：只有后端返回了 other 时才显示
    // 使用 useState 保存初始状态，确保即使用户取消选择后，分类仍然显示
    useEffect(() => {
        // Premium 模式下始终显示自定义图分类
        if (modelVersion === 'premium') {
            setHasOtherFromBackend(true);
            hasOtherFromBackendRef.current[chapterKey] = true;
            return;
        }

        // Normal/Ultra 模式：检查后端是否返回了 other
        if (hasOtherFromBackendRef.current[chapterKey]) {
            setHasOtherFromBackend(hasOtherFromBackendRef.current[chapterKey]);
            return;
        }

        // 检查原始 selectedChartType 是否包含 other（后端返回的初始值）
        const hasOtherInSelected =
            selectedChartType &&
            ((typeof selectedChartType === 'string' &&
                selectedChartType.includes(ChartType.OTHER)) ||
                (Array.isArray(selectedChartType) && selectedChartType.includes(ChartType.OTHER)));
        // 检查 chartTypeCn 中是否有 other（后端返回的初始值）
        // 只要 chartTypeCn.other 存在（即使是空字符串），就认为后端返回了 other
        const hasOtherInChartTypeCn = chartTypeCn?.other !== undefined;

        // 一旦检测到后端返回了 other，就永远保存这个标志（针对当前章节）
        const hasOther = hasOtherInSelected || hasOtherInChartTypeCn;
        if (hasOther) {
            hasOtherFromBackendRef.current[chapterKey] = true;
            setHasOtherFromBackend(true);
        } else {
            hasOtherFromBackendRef.current[chapterKey] = false;
            setHasOtherFromBackend(false);
        }
    }, [selectedChartType, chartTypeCn, chapterKey, modelVersion]);

    const filteredChartGroups = useMemo(() => {
        let groups = CHART_GROUPS; // 移除自定义图分组后，直接使用所有分组

        // 如果有搜索关键词，进行搜索过滤
        if (searchKeyword.trim()) {
            const keyword = searchKeyword.trim().toLowerCase();
            groups = groups
                .map((group) => {
                    const filteredCharts = group.charts.filter(
                        (chart) =>
                            chart.label.toLowerCase().includes(keyword) ||
                            group.title.toLowerCase().includes(keyword),
                    );
                    return filteredCharts.length > 0 ? { ...group, charts: filteredCharts } : null;
                })
                .filter(Boolean);
        }

        return groups;
    }, [searchKeyword]);

    // 判断是否为一级大纲（一级大纲有 sections 属性）
    const isLevel1 = data?.sections !== undefined;
    // 判断是否为开题报告或文献综述
    const isKaitiOrZongshu =
        docType === DocType.PROPOSAL_REPORT || docType === DocType.LITERATURE_REVIEW;
    // 对于一级大纲，在开题报告和文献综述时，且为最后一级时，才显示表和图按钮
    const canShowTableOrDiagramForLevel1 = isLevel1 && isKaitiOrZongshu && isLastLevel;

    const codeIcon = useMemo(() => {
        // 如果指定了 allowedExtraTypes 且不包含 CODE，则不显示
        if (allowedExtraTypes && !allowedExtraTypes.includes('CODE')) {
            return null;
        }
        const { canShow, have, tip } = judgeIcon(
            { extraType, selected_extra, extraTypeCn },
            'CODE',
        );
        if (!canShow) return null;
        return (
            <Tooltip title={tip}>
                <div
                    className={cx(styles.iconBtn, have ? styles.iconActive : '')}
                    onClick={() => onChangeExtra?.({ checked: !have, type: 'CODE' })}
                    data-tour-icon="CODE"
                >
                    {CodeIcon}
                </div>
            </Tooltip>
        );
    }, [extraType, selected_extra, extraTypeCn, onChangeExtra, allowedExtraTypes]);

    const tableIcon = useMemo(() => {
        // 如果指定了 allowedExtraTypes 且不包含 TABLE，则不显示
        if (allowedExtraTypes && !allowedExtraTypes.includes('TABLE')) {
            return null;
        }
        const { canShow, have, tip } = judgeIcon(
            { extraType, selected_extra, extraTypeCn },
            'TABLE',
            { isLevel1, isKaitiOrZongshu, isLastLevel },
        );
        if (!canShow) return null;
        // 对于一级大纲，在开题报告和文献综述时且为最后一级时显示，否则需要 isLastLevel
        const shouldShow = canShowTableOrDiagramForLevel1 || isLastLevel;
        if (!shouldShow) return null;
        return (
            <Tooltip title={tip}>
                <div
                    className={cx(styles.iconBtn, have ? styles.iconActive : '')}
                    onClick={() => onChangeExtra?.({ checked: !have, type: 'TABLE' })}
                    data-tour-icon="TABLE"
                >
                    {TableIcon}
                </div>
            </Tooltip>
        );
    }, [
        extraType,
        selected_extra,
        extraTypeCn,
        onChangeExtra,
        allowedExtraTypes,
        canShowTableOrDiagramForLevel1,
        isLastLevel,
        isLevel1,
        isKaitiOrZongshu,
    ]);

    // 生成图表类型的 tooltip 文案
    const getChartTooltip = useMemo(() => {
        if (chartTypeCn && typeof chartTypeCn === 'object' && Object.keys(chartTypeCn).length > 0) {
            // 如果有 chartTypeCn，显示所有选中图表类型的文案
            return Object.values(chartTypeCn).join('\n');
        }
        return null;
    }, [chartTypeCn]);

    // 当 chartTypeCn.other 变化时，同步更新输入框的值
    useEffect(() => {
        if (chartTypeCn?.other !== undefined && chartTypeCn.other !== otherChartInputValue) {
            setOtherChartInputValue(chartTypeCn.other);
        }
    }, [chartTypeCn?.other]);

    // 当自定义图有内容时，默认展开输入框（仅在初始化时）
    useEffect(() => {
        const hasContent = chartTypeCn?.other || otherChartInputValue;
        if (
            hasContent &&
            !hasInitializedExpandRef.current &&
            expandedChartType !== ChartType.OTHER
        ) {
            setExpandedChartType(ChartType.OTHER);
            hasInitializedExpandRef.current = true;
        }
        // 如果内容被清空，重置初始化标记
        if (!hasContent) {
            hasInitializedExpandRef.current = false;
        }
    }, [chartTypeCn?.other, otherChartInputValue, expandedChartType]);

    // 当展开自定义图表编辑区域时，将光标移动到文本末尾
    useEffect(() => {
        if (expandedChartType === ChartType.OTHER && customChartTextareaRef.current) {
            const textarea = customChartTextareaRef.current;
            // 使用 setTimeout 确保 DOM 已经渲染完成
            setTimeout(() => {
                textarea.focus();
                const length = textarea.value.length;
                textarea.setSelectionRange(length, length);
            }, 0);
        }
    }, [expandedChartType]);

    // 获取所有图表配置的映射（用于查找图表标签）
    const allChartsMap = useMemo(() => {
        const map = {};
        CHART_GROUPS.forEach((group) => {
            group.charts.forEach((chart) => {
                map[chart.type] = chart;
            });
        });
        return map;
    }, []);

    // 构建新的 chartTypeCn 对象（通用函数）
    const buildChartTypeCn = useCallback(
        (types, useInputValue = false) => {
            const newChartTypeCn = {};
            types.forEach((type) => {
                if (type === ChartType.OTHER) {
                    // 对于 other 类型，根据参数决定使用输入框的值还是保留原有值
                    if (useInputValue) {
                        newChartTypeCn[type] = otherChartInputValue || chartTypeCn?.other || '';
                    } else {
                        newChartTypeCn[type] = chartTypeCn?.other || '自定义图表';
                    }
                } else if (chartTypeCn && chartTypeCn[type]) {
                    // 保留原有的文案
                    newChartTypeCn[type] = chartTypeCn[type];
                } else {
                    // 如果没有对应的文案，使用默认标签
                    const chartConfig = allChartsMap[type];
                    if (chartConfig) {
                        newChartTypeCn[type] = chartConfig.label;
                    }
                }
            });
            return newChartTypeCn;
        },
        [chartTypeCn, allChartsMap, otherChartInputValue],
    );

    // 处理图表类型选择/取消选择
    const handleChartTypeToggle = useCallback(
        (chartType, isSelected) => {
            const newSelectedTypes = isSelected
                ? selectedChartTypes.filter((t) => t !== chartType)
                : [...selectedChartTypes, chartType];

            // 构建新的 chartTypeCn 对象
            const newChartTypeCn = buildChartTypeCn(newSelectedTypes, true);

            // 即使取消选择，也要保留 chartTypeCn.other 的值（如果存在）
            const finalChartTypeCn =
                newSelectedTypes.length > 0
                    ? newChartTypeCn
                    : chartTypeCn?.other
                      ? { [ChartType.OTHER]: chartTypeCn.other }
                      : null;

            onChangeExtra?.({
                type: 'CHART',
                chartTypes: newSelectedTypes,
                chartTypeCn: finalChartTypeCn,
            });
        },
        [selectedChartTypes, buildChartTypeCn, chartTypeCn, onChangeExtra],
    );

    // 处理自定义图表的创建（Premium模式）
    const handleCreateCustomChart = useCallback(() => {
        if (modelVersion === 'premium') {
            // 如果还没有选中other，先选中它
            if (!selectedChartTypes.includes(ChartType.OTHER)) {
                handleChartTypeToggle(ChartType.OTHER, false);
            }
            // 自动展开编辑
            setExpandedChartType(ChartType.OTHER);
        }
    }, [modelVersion, selectedChartTypes, handleChartTypeToggle]);

    // 处理 other 类型的文本编辑（onChange）
    const handleOtherTextChange = useCallback(
        (value) => {
            setOtherChartInputValue(value);

            // Premium模式下，如果用户输入了值但还没有选中other，先自动选中
            let currentSelectedTypes = selectedChartTypes;
            if (
                value &&
                modelVersion === 'premium' &&
                !selectedChartTypes.includes(ChartType.OTHER)
            ) {
                // 自动选中 other 类型
                currentSelectedTypes = [...selectedChartTypes, ChartType.OTHER];
                // 构建新的 chartTypeCn，包含当前输入的值
                const newChartTypeCn = { ...chartTypeCn };
                newChartTypeCn[ChartType.OTHER] = value;

                // 保留其他已选中的图表类型的文案
                selectedChartTypes.forEach((type) => {
                    if (chartTypeCn && chartTypeCn[type]) {
                        newChartTypeCn[type] = chartTypeCn[type];
                    } else {
                        const chartConfig = allChartsMap[type];
                        if (chartConfig) {
                            newChartTypeCn[type] = chartConfig.label;
                        }
                    }
                });

                // 直接调用 onChangeExtra，不通过 handleChartTypeToggle（避免重复调用）
                onChangeExtra?.({
                    type: 'CHART',
                    chartTypes: currentSelectedTypes,
                    chartTypeCn: newChartTypeCn,
                });
                return; // 提前返回，避免下面的逻辑重复执行
            }

            // 如果 other 类型已选中，实时更新 chartTypeCn
            if (selectedChartTypes.includes(ChartType.OTHER)) {
                const newChartTypeCn = { ...chartTypeCn };
                if (value) {
                    // 如果输入框有值，更新 chartTypeCn.other
                    newChartTypeCn[ChartType.OTHER] = value;
                } else if (chartTypeCn?.other) {
                    // 如果输入框为空，保留原来的值（不删除）
                    newChartTypeCn[ChartType.OTHER] = chartTypeCn.other;
                }

                // 保留其他已选中的图表类型的文案
                selectedChartTypes.forEach((type) => {
                    if (type !== ChartType.OTHER) {
                        if (chartTypeCn && chartTypeCn[type]) {
                            newChartTypeCn[type] = chartTypeCn[type];
                        } else {
                            const chartConfig = allChartsMap[type];
                            if (chartConfig) {
                                newChartTypeCn[type] = chartConfig.label;
                            }
                        }
                    }
                });

                onChangeExtra?.({
                    type: 'CHART',
                    chartTypes: selectedChartTypes,
                    chartTypeCn: newChartTypeCn,
                });
            }
        },
        [selectedChartTypes, chartTypeCn, allChartsMap, onChangeExtra, modelVersion],
    );

    // 切换展开/收起（用于编辑自定义图表）
    const handleToggleExpand = useCallback((type) => {
        setExpandedChartType((prev) => (prev === type ? null : type));
    }, []);

    // 删除已选图表
    const handleRemoveChart = useCallback(
        (chartType) => {
            handleChartTypeToggle(chartType, true); // true 表示已选中，执行取消选择
            // 如果删除的是当前展开的，关闭展开状态
            if (expandedChartType === chartType) {
                setExpandedChartType(null);
            }
        },
        [handleChartTypeToggle, expandedChartType],
    );

    // 添加新图表（普通图表只能选中，不能取消选中）
    const handleAddChart = useCallback(
        (chartType) => {
            // 普通图表：如果已经选中，不做任何操作（不能取消选中）
            if (chartType !== ChartType.OTHER && selectedChartTypes.includes(chartType)) {
                return;
            }

            // 如果是 other 类型且是 premium，选中后自动展开编辑
            if (chartType === ChartType.OTHER && modelVersion === 'premium') {
                handleChartTypeToggle(chartType, false);
                setExpandedChartType(chartType);
            } else {
                // 普通图表：只允许选中（isSelected=false 表示当前未选中，执行选中操作）
                handleChartTypeToggle(chartType, false);
            }
        },
        [handleChartTypeToggle, modelVersion, selectedChartTypes],
    );

    // 图表选择器内容 - 两区域布局：已选列表 + 可选网格
    const chartSelectorContent = useMemo(
        () => (
            <div className={styles.chartSelector}>
                {/* 上半部：已选列表 */}
                {(selectedChartTypes.length > 0 ||
                    modelVersion === 'premium' ||
                    (hasOtherFromBackend && chartTypeCn?.other !== undefined)) && (
                    <div className={styles.selectedSection}>
                        <div className={styles.sectionHeader}>
                            本章节包含图表 (
                            {selectedChartTypes.filter((type) => type !== ChartType.OTHER).length +
                                (modelVersion === 'premium' ||
                                (hasOtherFromBackend && chartTypeCn?.other !== undefined)
                                    ? 1
                                    : 0)}
                            )
                        </div>
                        <div className={styles.selectedList}>
                            {/* 渲染已选的标准图表（排除自定义图表，自定义图表单独渲染） */}
                            {selectedChartTypes
                                .filter((type) => type !== ChartType.OTHER)
                                .map((type) => {
                                    const chartConfig = allChartsMap[type];
                                    const IconComponent = chartConfig?.icon;
                                    const displayName = chartTypeCn?.[type] || chartConfig?.label;

                                    return (
                                        <div key={type} className={styles.listItem}>
                                            <div className={styles.listItemInner}>
                                                <div className={styles.itemInfo}>
                                                    {IconComponent && (
                                                        <div className={styles.itemIcon}>
                                                            <IconComponent size={16} />
                                                        </div>
                                                    )}
                                                    <span className={styles.itemName}>
                                                        {displayName}
                                                    </span>
                                                </div>
                                                <div className={styles.actionGroup}>
                                                    <span
                                                        className={styles.btnRemove}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveChart(type);
                                                        }}
                                                    >
                                                        ×
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                            {/* 自定义图表项（根据modelVersion和hasOtherFromBackend显示） */}
                            {/* Premium模式：始终显示自定义图表项（即使没有值也可以编辑） */}
                            {/* Normal/Ultra模式：只有后端返回了other时才显示 */}
                            {(modelVersion === 'premium' ||
                                (hasOtherFromBackend && chartTypeCn?.other !== undefined)) && (
                                <div
                                    className={cx(styles.listItem, {
                                        [styles.expanded]: expandedChartType === ChartType.OTHER,
                                    })}
                                >
                                    <div className={styles.listItemInner}>
                                        <div className={styles.itemInfo}>
                                            <div className={styles.itemIcon}>✨</div>
                                            <span className={styles.itemName}>自定义图</span>
                                            {modelVersion === 'premium' && (
                                                <span className={styles.aiBadge}>AI</span>
                                            )}
                                        </div>
                                        <div className={styles.actionGroup}>
                                            {/* Premium模式：始终可编辑 */}
                                            {/* Normal/Ultra模式：只有后端返回了other时才可编辑 */}
                                            {(modelVersion === 'premium' ||
                                                (hasOtherFromBackend &&
                                                    chartTypeCn?.other !== undefined)) && (
                                                <span
                                                    className={styles.btnEdit}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleExpand(ChartType.OTHER);
                                                    }}
                                                >
                                                    {expandedChartType === ChartType.OTHER
                                                        ? '收起'
                                                        : '编辑'}
                                                </span>
                                            )}
                                            {/* Premium模式：始终可删除 */}
                                            {/* Normal/Ultra模式：只有后端返回了other时才可删除 */}
                                            {(modelVersion === 'premium' ||
                                                (hasOtherFromBackend &&
                                                    chartTypeCn?.other !== undefined)) && (
                                                <span
                                                    className={styles.btnRemove}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveChart(ChartType.OTHER);
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 编辑区域 */}
                                    {expandedChartType === ChartType.OTHER &&
                                        (modelVersion === 'premium' ||
                                            (hasOtherFromBackend &&
                                                chartTypeCn?.other !== undefined)) && (
                                            <div className={styles.editArea}>
                                                <textarea
                                                    ref={customChartTextareaRef}
                                                    className={styles.promptBox}
                                                    placeholder="请输入自定义图表名称"
                                                    value={otherChartInputValue}
                                                    onChange={(e) =>
                                                        handleOtherTextChange(e.target.value)
                                                    }
                                                    disabled={modelVersion !== 'premium'}
                                                    autoFocus
                                                    rows={3}
                                                />
                                            </div>
                                        )}
                                </div>
                            )}
                            {/* Premium模式：如果没有自定义图表，显示创建按钮 */}
                            {modelVersion === 'premium' &&
                                !hasOtherFromBackend &&
                                !chartTypeCn?.other && (
                                    <div className={styles.listItem}>
                                        <div className={styles.listItemInner}>
                                            <div className={styles.itemInfo}>
                                                <div className={styles.itemIcon}>✨</div>
                                                <span className={styles.itemName}>自定义图</span>
                                                <span className={styles.aiBadge}>AI</span>
                                            </div>
                                            <div className={styles.actionGroup}>
                                                <span
                                                    className={styles.btnEdit}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCreateCustomChart();
                                                    }}
                                                >
                                                    创建
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                {/* 搜索区域 */}
                <div className={styles.inputSection}>
                    <div className={styles.searchWrapper}>
                        <span className={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            className={styles.mainInput}
                            placeholder="输入 '柱状图' 筛选"
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                        />
                    </div>
                </div>

                {/* 图表库区域 */}
                <div className={styles.librarySection}>
                    {/* 如果有搜索关键词，显示搜索结果 */}
                    {searchKeyword.trim()
                        ? filteredChartGroups.map((group) => (
                              <div key={group.title}>
                                  <div className={styles.categoryTitle}>{group.title}</div>
                                  <div className={styles.chartGrid}>
                                      {group.charts
                                          .filter(
                                              (chart) => chart.type !== ChartType.OTHER, // 过滤掉自定义图表
                                          )
                                          .map((chart) => {
                                              const IconComponent = chart.icon;
                                              const isSelected = selectedChartTypes.includes(
                                                  chart.type,
                                              );

                                              return (
                                                  <div
                                                      key={chart.type}
                                                      className={cx(styles.chartItem, {
                                                          [styles.active]: isSelected,
                                                      })}
                                                      onClick={() =>
                                                          !isSelected && handleAddChart(chart.type)
                                                      }
                                                  >
                                                      <div className={styles.iconBox}>
                                                          <IconComponent size={20} />
                                                      </div>
                                                      <div className={styles.chartName}>
                                                          {chart.label}
                                                      </div>
                                                  </div>
                                              );
                                          })}
                                  </div>
                              </div>
                          ))
                        : /* 没有搜索时，显示所有分组 */
                          filteredChartGroups.map((group) => (
                              <div key={group.title}>
                                  <div className={styles.categoryTitle}>{group.title}</div>
                                  <div className={styles.chartGrid}>
                                      {group.charts
                                          .filter(
                                              (chart) => chart.type !== ChartType.OTHER, // 过滤掉自定义图表
                                          )
                                          .map((chart) => {
                                              const IconComponent = chart.icon;
                                              const isSelected = selectedChartTypes.includes(
                                                  chart.type,
                                              );

                                              return (
                                                  <div
                                                      key={chart.type}
                                                      className={cx(styles.chartItem, {
                                                          [styles.active]: isSelected,
                                                      })}
                                                      onClick={() =>
                                                          !isSelected && handleAddChart(chart.type)
                                                      }
                                                  >
                                                      <div className={styles.iconBox}>
                                                          <IconComponent size={20} />
                                                      </div>
                                                      <div className={styles.chartName}>
                                                          {chart.label}
                                                      </div>
                                                  </div>
                                              );
                                          })}
                                  </div>
                              </div>
                          ))}
                </div>
            </div>
        ),
        [
            selectedChartTypes,
            chartTypeCn,
            allChartsMap,
            expandedChartType,
            otherChartInputValue,
            modelVersion,
            filteredChartGroups,
            hasOtherFromBackend,
            searchKeyword,
            handleToggleExpand,
            handleRemoveChart,
            handleAddChart,
            handleOtherTextChange,
            handleCreateCustomChart,
        ],
    );

    // 流程图 - 集成图表选择功能
    const diagramIcon = useMemo(() => {
        // 如果指定了 allowedExtraTypes 且不包含 DIAGRAM，则不显示
        if (allowedExtraTypes && !allowedExtraTypes.includes('DIAGRAM')) {
            return null;
        }
        const { canShow, have, tip } = judgeIcon(
            { extraType, selected_extra, extraTypeCn },
            'DIAGRAM',
            { isLevel1, isKaitiOrZongshu, isLastLevel },
        );
        if (!canShow) return null;
        // 对于一级大纲，在开题报告和文献综述时且为最后一级时显示，否则需要 isLastLevel
        const shouldShow = canShowTableOrDiagramForLevel1 || isLastLevel;
        if (!shouldShow) return null;

        // 确定 tooltip 文案
        const tooltipText = getChartTooltip || tip;
        const isSelected = have || selectedChartTypes.length > 0;

        return (
            <Popover
                content={chartSelectorContent}
                trigger={'click'}
                placement="bottomLeft"
                open={chartPopoverVisible}
                onOpenChange={setChartPopoverVisible}
                overlayClassName={styles.chartPopover}
                styles={{
                    body: {
                        padding: '0px',
                    },
                }}
            >
                <Tooltip title={tooltipText} placement="bottom">
                    <div
                        className={cx(styles.iconBtn, {
                            [styles.iconActive]: isSelected,
                        })}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        data-tour-icon="DIAGRAM"
                    >
                        {PicIcon}
                    </div>
                </Tooltip>
            </Popover>
        );
    }, [
        extraType,
        selected_extra,
        extraTypeCn,
        onChangeExtra,
        selectedChartTypes,
        chartTypeCn,
        getChartTooltip,
        chartPopoverVisible,
        chartSelectorContent,
        allowedExtraTypes,
        canShowTableOrDiagramForLevel1,
        isLastLevel,
        isLevel1,
        isKaitiOrZongshu,
    ]);

    const formulaIcon = useMemo(() => {
        // 如果指定了 allowedExtraTypes 且不包含 FORMULA，则不显示
        if (allowedExtraTypes && !allowedExtraTypes.includes('FORMULA')) {
            return null;
        }
        const { canShow, have, tip } = judgeIcon(
            { extraType, selected_extra, extraTypeCn },
            'FORMULA',
        );
        if (!canShow) return null;
        return (
            <Tooltip title={tip}>
                <div
                    className={cx(styles.iconBtn, have ? styles.iconActive : '')}
                    onClick={() => onChangeExtra?.({ checked: !have, type: 'FORMULA' })}
                    data-tour-icon="FORMULA"
                >
                    {GongshiIcon}
                </div>
            </Tooltip>
        );
    }, [extraType, selected_extra, extraTypeCn, onChangeExtra, allowedExtraTypes]);

    const dataIcon = useMemo(() => {
        // 如果指定了 allowedExtraTypes 且不包含 DATA，则不显示
        if (allowedExtraTypes && !allowedExtraTypes.includes('DATA')) {
            return null;
        }
        const { canShow, have, tip } = judgeIcon(
            { extraType, selected_extra, extraTypeCn },
            'DATA',
        );
        if (!canShow) return null;
        return (
            <Tooltip title={tip}>
                <div
                    className={cx(styles.iconBtn, have ? styles.iconActive : '')}
                    onClick={() => onChangeExtra?.({ checked: !have, type: 'DATA' })}
                    data-tour-icon="DATA"
                >
                    {DataIcon}
                </div>
            </Tooltip>
        );
    }, [extraType, selected_extra, extraTypeCn, onChangeExtra, allowedExtraTypes]);

    return (
        <div className={cx(styles.opt, className)}>
            {/* 表和图按钮：对于一级大纲，在开题报告和文献综述时显示；其他情况需要 isLastLevel */}
            {tableIcon}
            {diagramIcon}
            {/* 数据、公式和代码按钮只在 isLastLevel 时显示 */}
            {isLastLevel && (
                <>
                    {dataIcon}
                    {formulaIcon}
                    {codeIcon}
                </>
            )}
            <Tooltip title="添加章节">
                <Button
                    type="text"
                    className={styles.iconBtn}
                    icon={AddIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        onAdd?.();
                    }}
                />
            </Tooltip>
            {!hideEdit && onEdit && (
                <Tooltip title="编辑章节">
                    <Button
                        className={styles.iconBtn}
                        type="text"
                        icon={EditIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.();
                        }}
                    />
                </Tooltip>
            )}

            {!hideDelete && onDelete && (
                <Tooltip title="删除章节">
                    <Button
                        className={styles.iconBtn}
                        type="text"
                        icon={DeleteIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.();
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
});
